/**
 * Smart Editor Utilities Module
 * Helper functions for editor configuration
 *
 * @module SmartEditorUtils
 */

import {
  EDITOR_MENUBAR,
  EDITOR_PLUGINS,
  EDITOR_REMOVED_MENUITEMS,
  EDITOR_TOOLBAR,
  SmartEditorProps,
  SmartEditorType,
} from '.';

/**
 * Join array or string items into a single string
 * Handles different input types and formats them for editor configuration
 *
 * @param {string[] | string | boolean | readonly string[]} items - Items to join
 * @returns {string} Joined string with pipe separator
 *
 * @example
 * joinItems(['bold', 'italic']) // 'bold | italic'
 * joinItems(true) // 'true'
 */
export const joinItems = (items: string[] | string | boolean | readonly string[]): string => {
  if (typeof items === 'boolean') return items ? 'true' : 'false';
  if (Array.isArray(items)) return [...items].join(' | ');
  return String(items);
};

/**
 * Get menubar configuration based on editor type
 * Returns custom menubar if provided, otherwise uses default configuration
 *
 * @param {SmartEditorType} type - Editor type (source/target)
 * @param {string | boolean} [menubar] - Optional custom menubar configuration
 * @returns {string | boolean} Menubar configuration
 *
 * @example
 * getMenubarByType('source') // Returns default source menubar
 * getMenubarByType('target', 'file edit') // Returns 'file edit'
 */
export const getMenubarByType = (
  type: SmartEditorType,
  menubar?: string | boolean,
): string | boolean => {
  if (menubar !== undefined) return menubar;
  return EDITOR_MENUBAR[type];
};

/**
 * Get plugins configuration based on editor type
 * Returns custom plugins if provided, otherwise uses default configuration
 *
 * @param {SmartEditorType} type - Editor type (source/target)
 * @param {SmartEditorProps['plugins']} [plugins] - Optional custom plugins
 * @returns {string} Plugins configuration string
 *
 * @example
 * getPluginsByType('source') // Returns default source plugins
 * getPluginsByType('target', 'table paste') // Returns 'table paste'
 */
export const getPluginsByType = (
  type: SmartEditorType,
  plugins?: SmartEditorProps['plugins'],
): string => {
  if (plugins !== undefined) return plugins as string;
  return joinItems(EDITOR_PLUGINS[type]);
};

/**
 * Get toolbar configuration based on editor type
 * Returns custom toolbar if provided, otherwise uses default configuration
 *
 * @param {SmartEditorType} type - Editor type (source/target)
 * @param {SmartEditorProps['toolbar']} [toolbar] - Optional custom toolbar
 * @returns {string} Toolbar configuration string
 *
 * @example
 * getToolbarByType('source') // Returns default source toolbar
 * getToolbarByType('target', 'bold italic') // Returns 'bold italic'
 */
export const getToolbarByType = (
  type: SmartEditorType,
  toolbar?: SmartEditorProps['toolbar'],
): string => {
  if (toolbar !== undefined) return toolbar as string;
  return joinItems(EDITOR_TOOLBAR[type]);
};

/**
 * Get removed menu items configuration based on editor type
 * Returns custom configuration if provided, otherwise uses default
 *
 * @param {SmartEditorType} type - Editor type (source/target)
 * @param {string} [removedMenuitems] - Optional custom removed menu items
 * @returns {string | undefined} Removed menu items configuration
 *
 * @example
 * getRemovedMenuitemsByType('source') // Returns default source removed items
 * getRemovedMenuitemsByType('target', 'paste') // Returns 'paste'
 */
export const getRemovedMenuitemsByType = (
  type: SmartEditorType,
  removedMenuitems?: string,
): string | undefined => {
  if (removedMenuitems !== undefined) return removedMenuitems;
  const items = EDITOR_REMOVED_MENUITEMS[type];
  return items ? [...items].join(', ') : undefined;
};

const closestTableCell = (node: Node | null): HTMLElement | null => {
  const el =
    node?.nodeType === Node.ELEMENT_NODE ? (node as Element) : (node?.parentElement ?? null);
  return el?.closest('td, th') ?? null;
};

/**
 * Collect distinct `data-nodeid` values for table cells touched by a DOM Range.
 * Used so drag-selection across multiple cells maps to multi-cell target selection (transfer).
 */
export const getTableCellDataNodeIdsInRange = (range: Range): string[] => {
  const startCell = closestTableCell(range.startContainer);
  const endCell = closestTableCell(range.endContainer);

  const rootEl =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? (range.commonAncestorContainer as Element)
      : range.commonAncestorContainer.parentElement;

  const table =
    rootEl?.closest('table') ?? startCell?.closest('table') ?? endCell?.closest('table');

  if (!table) {
    const id = startCell?.getAttribute('data-nodeid');
    return id ? [id] : [];
  }

  const cells = Array.from(table.querySelectorAll<HTMLElement>('td, th'));
  const ids: string[] = [];
  for (const cell of cells) {
    if (range.intersectsNode(cell)) {
      const id = cell.getAttribute('data-nodeid');
      if (id) ids.push(id);
    }
  }

  if (ids.length > 0) {
    return [...new Set(ids)];
  }

  const fallback = [startCell, endCell]
    .map((c) => c?.getAttribute('data-nodeid'))
    .filter((id): id is string => !!id);
  return [...new Set(fallback)];
};
